<template>
    <div class="fx-grid">
        <div v-for="(video, index) in displayedVideos" 
            :key="video.id || index"
            class="fx-card fx-item fx-span-6@sm-md fx-span-3@lg cursor-pointer" 
            tabindex="0" 
            role="button"
            :title="`Video, ${video.title}`" 
            @click="openVideoModal(video)" 
            @keydown.enter="openVideoModal(video)">
            <div class="fx-card__image fx-card__image--ar19-12 video__overlay">
                <img :src="video.thumb" :alt="`Video, ${video.title}`" />
            </div>
            <div class="fx-card__content">
                <h3 class="fx-card__content__title">{{ video.title }}</h3>
                <span class="fx-card__content__cta u-mrg-top-half--nobp">Watch Video</span>
            </div>
        </div>
        <button v-if="showLoadMoreButton" @click="loadMore" class="fx-item btn btn--secondary show-more-button">Load More</button>
    </div>

    <!-- Single Modal Dialog -->
    <a11y-dialog 
        :id="`video-modal-${uuid}`" 
        dialog-root="#vue-dialog-root" 
        @dialog-ref="(dialog) => assignDialogRef(dialog)" 
        :class-names="{ container: 'dialog', overlay: 'fe-modal-overlay' }">

        <div class="dialog-content fe-modal">
            <button type="button" 
                data-a11y-dialog-hide 
                @click="closeModal" 
                @keydown.enter="closeModal"
                aria-label="Close this dialog window" 
                class="fe-modal-close">
                &times;
            </button>
            <div v-if="modalShow && selectedVideo">
                <video-modal v-if="isVideoUrl(selectedVideo.source)" 
                    :src="selectedVideo.source" 
                    :showTitle="true" 
                    :title="selectedVideo.title">
                </video-modal>
                <you-tube-modal v-if="!isVideoUrl(selectedVideo.source)" 
                    :id="selectedVideo.source" 
                    :showTitle="true" 
                    :title="selectedVideo.title" 
                    :start="selectedVideo.start || ''"
                    :lang="youTubeLang">
                </you-tube-modal>
            </div>
        </div>
    </a11y-dialog>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import VideoModal from './VideoModal.vue'
import YouTubeModal from './YouTubeModal.vue'

// Props
const props = defineProps({
    model: {
        type: Array,
        default: () => []
    },
    youTubeLang: {
        type:String,
        default:""
    },
    pageSize: {
        type: Number,
        default: 4
    }
})

// Reactive data
const currentPage = ref(1)
const modalShow = ref(false)
const selectedVideo = ref(null)
const dialog = ref(null)
const uuid = ref(`video-list-${Date.now()}-${Math.floor(Math.random() * 1000000)}`)

// Helper function to determine if source is a video URL
const isVideoUrl = (source) => {
    if (!source || typeof source !== 'string') {
        return false
    }
    
    // Check if it looks like a URL (contains protocol or starts with /)
    const urlPattern = /^(https?:\/\/|\/)/i
    const videoExtensions = /\.(mp4|webm|ogg|avi|mov|wmv|flv|mkv)(\?.*)?$/i
    
    return urlPattern.test(source) || videoExtensions.test(source)
}

// Computed property to get videos from the model
const videos = computed(() => {
    if (!props.model || !Array.isArray(props.model)) {
        return []
    }

    return props.model
})

// Computed property for displayed videos
const displayedVideos = computed(() => {
    const endIndex = currentPage.value * props.pageSize
    return videos.value.slice(0, endIndex)
})

// Computed property to determine if Load More button should be shown
const showLoadMoreButton = computed(() => {
    const totalVideos = videos.value.length
    const displayedCount = displayedVideos.value.length
    
    // Don't show if total videos is <= pageSize
    if (totalVideos <= props.pageSize) {
        return false
    }
    
    // Don't show if all videos are already displayed
    return displayedCount < totalVideos
})

// Method to load more videos
const loadMore = () => {
    currentPage.value++
}

// Modal methods
const openVideoModal = (video) => {
    selectedVideo.value = video
    modalShow.value = true
    if (dialog.value) {
        dialog.value.show()
    }
}

const closeModal = () => {
    modalShow.value = false
    selectedVideo.value = null
    if (dialog.value) {
        dialog.value.hide()
    }
}

const assignDialogRef = (dialogRef) => {
    if (dialogRef) {
        dialog.value = dialogRef
        dialog.value.on('hide', closeModal)
        dialog.value.on('destroy', () => dialog.value.off('hide', closeModal))
    }
}

onMounted(() => {
    // console.log('Total number of videos:', videos.value.length)
    // console.log('YouTube lang parameter:', props.youTubeLang)
})

// Expose videos and pagination state for potential parent component access
defineExpose({
    videos,
    displayedVideos,
    currentPage,
    loadMore,
    openVideoModal,
    closeModal,
    selectedVideo
})
</script>
